import { ExpandLess, ExpandMore } from '@mui/icons-material'
import { Stack, Box, IconButton, Typography } from '@mui/material'
import { Theme } from '@mui/material/styles'
import { SwitchLabeled } from '@ui/actions'

interface BaseProps {
  children: React.ReactNode
  label: string
  isExpanded: boolean
  startIcon?: React.ReactNode
  dataCy?: string
  headerBackgroundColor?: string | ((theme: Theme) => string)
  headerTextColor?: string | ((theme: Theme) => string)
}

interface SwitchVariantProps extends BaseProps {
  variant: 'switch'
  handleToggle: (event: React.ChangeEvent<HTMLInputElement>) => void
}

interface ExpanderVariantProps extends BaseProps {
  variant: 'expander'
  handleToggle: (event: React.MouseEvent<HTMLButtonElement>) => void
}

const defaultBoxStyles = {
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  border: '1px solid',
  borderColor: 'grey.300',
  borderRadius: '5px',
  paddingX: '0px',
  paddingY: '0px',
  paddingBottom: '4px',
  backgroundColor: 'transparent',
  width: '100%',
}

const defaultHeaderBackgroundColor = '#EEEEEE'
const defaultHeaderTextColor = '#000000'
type Props = SwitchVariantProps | ExpanderVariantProps

const CollapsibleBox = ({
  children,
  variant,
  label,
  isExpanded,
  handleToggle,
  startIcon,
  dataCy,
  headerBackgroundColor,
  headerTextColor,
}: Props) => {
  if (variant === 'switch') {
    return (
      <Box sx={defaultBoxStyles} data-cy={dataCy}>
        <Stack
          direction="column"
          spacing={1}
          alignItems="center"
          justifyContent={'space-between'}
          width="100%"
        >
          {startIcon && startIcon}
          {variant === 'switch' && (
            <SwitchLabeled
              checked={isExpanded}
              onChange={handleToggle}
              label={label}
              data-cy={`${dataCy}-switch`}
            />
          )}
          {isExpanded && children}
        </Stack>
      </Box>
    )
  } else if (variant === 'expander') {
    return isExpanded ? (
      <Box sx={defaultBoxStyles} data-cy={dataCy}>
        <Stack
          direction="column"
          spacing={0}
          alignItems="center"
          justifyContent={'space-between'}
          width="100%"
        >
          <Stack
            direction="row"
            spacing={0}
            alignItems="center"
            sx={{
              width: '100%',
              backgroundColor: headerBackgroundColor ?? defaultHeaderBackgroundColor,
            }}
            paddingX="4px"
          >
            <Stack
              direction="row"
              spacing={0.5}
              alignItems="center"
              paddingY="4px"
              sx={{ width: '100%' }}
            >
              {startIcon && startIcon}
              <IconButton
                onClick={handleToggle}
                aria-expanded={isExpanded}
                data-cy={`${dataCy}-expander`}
                sx={{ padding: 0 }}
              >
                {isExpanded ? <ExpandLess /> : <ExpandMore />}
              </IconButton>
              <Typography
                fontSize="inherit"
                sx={{ width: '100%', color: headerTextColor ?? defaultHeaderTextColor }}
              >
                {label}
              </Typography>
            </Stack>
          </Stack>
          <Box
            sx={{
              paddingLeft: '8px',
              paddingRight: '4px',
              paddingTop: '8px',
              paddingBottom: '8px',
              width: '100%',
            }}
          >
            {isExpanded && children}
          </Box>
        </Stack>
      </Box>
    ) : (
      <Box sx={{ ...defaultBoxStyles, paddingBottom: '0px' }} data-cy={dataCy}>
        <Stack
          direction="column"
          spacing={0}
          alignItems="center"
          justifyContent={'space-between'}
          width="100%"
        >
          <Stack
            direction="row"
            spacing={0}
            alignItems="center"
            sx={{
              width: '100%',
              backgroundColor: headerBackgroundColor ?? defaultHeaderBackgroundColor,
            }}
            paddingX="4px"
          >
            <Stack
              direction="row"
              spacing={0.5}
              alignItems="center"
              paddingY="4px"
              sx={{ width: '100%' }}
            >
              {startIcon && startIcon}
              <IconButton
                onClick={handleToggle}
                aria-expanded={isExpanded}
                data-cy={`${dataCy}-expander`}
                sx={{ padding: 0 }}
              >
                {isExpanded ? <ExpandLess /> : <ExpandMore />}
              </IconButton>
              <Typography
                fontSize="inherit"
                sx={{ width: '100%', color: headerTextColor ?? defaultHeaderTextColor }}
              >
                {label}
              </Typography>
            </Stack>
          </Stack>
        </Stack>
      </Box>
    )
  } else {
    return <> </>
  }
}

export default CollapsibleBox
